'use client'

import React, { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ArrowLeft, DeviceMobile, Users, Clock, TrendUp, CheckCircle, Lightning, Target, ChartBar, Package, ShoppingCart, Warning } from 'phosphor-react'
import { Link } from '@/lib'
import Components from '../components'

export default function StoreInventoryOperations() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  return (
    <Components.PageContainer>
      <Components.GlassNavigation />
      
      <main className="pt-16 md:pt-32 pb-16 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">

          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-[var(--brutal-ink)] mb-6 leading-tight">
              Store Inventory Operations
            </h1>
            <p className="text-xl text-[var(--brutal-muted)] mb-8 leading-relaxed max-w-4xl mx-auto">
              Mobile-first inventory management solution that streamlined store operations and inventory management with cross-product UX unification, reducing training time and improving operational efficiency.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Mobile Design</Badge>
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Store Operations</Badge>
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Inventory Management</Badge>
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Cross-Platform UX</Badge>
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Workflow Optimization</Badge>
            </div>
          </div>

          {/* Mobile App Preview Carousel */}
          <div className="mb-16">
            <Card variant="media" className="brutal-panel overflow-hidden !gap-0 !py-0">
              <Components.ImageCarousel
                images={[
                  {
                    src: "/assets/SOCS-mobile-fast.mp4",
                    alt: "Mobile curbside pickup interface showing order details, vehicle information, and inventory items ready for pickup",
                    title: "Curbside Pickup Interface",
                    description: "Streamlined mobile interface for curbside pickup operations",
                    isVideo: true,
                    poster: "/assets/SOCS-inventory-scanning.png"
                  },
                  {
                    src: "/assets/SOCS-inventory-scanning.png",
                    alt: "Mobile inventory scanning interface with barcode scanner and real-time updates",
                    title: "Inventory Scanning",
                    description: "Barcode scanning with real-time inventory updates and batch processing"
                  },
                  {
                    src: "/assets/SOCS-inventory-management.png",
                    alt: "Mobile order management interface showing order workflows and status tracking",
                    title: "Order Management",
                    description: "Simplified ordering workflows with predictive suggestions"
                  }
                ]}
              />
              <div className="p-4 md:p-6 text-center">
                <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-4">Mobile Inventory Interface</h3>
                <p className="text-[var(--brutal-muted)] mb-4">Streamlined mobile app for store inventory management and curbside pickup operations</p>
                <div className="grid grid-cols-2 gap-4 text-left text-[var(--brutal-muted)] text-sm max-w-md mx-auto">
                  <div>• Order tracking interface</div>
                  <div>• Vehicle identification system</div>
                  <div>• Real-time inventory updates</div>
                  <div>• Intuitive pickup workflows</div>
                </div>
              </div>
            </Card>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
            <Card className="brutal-panel p-4 md:p-6 text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 aspect-square brutal-chip mx-auto mb-3 md:mb-4 bg-[#8fce8f]">
                <Clock className="w-6 h-6 md:w-8 md:h-8 text-[var(--brutal-ink)] flex-shrink-0" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-1 md:mb-2 break-words">75%</div>
              <div className="text-[var(--brutal-muted)] text-xs md:text-sm break-words">Faster Training</div>
            </Card>
            
            <Card className="brutal-panel p-4 md:p-6 text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 aspect-square brutal-chip mx-auto mb-3 md:mb-4 bg-[var(--brutal-kraft)]">
                <Users className="w-6 h-6 md:w-8 md:h-8 text-[var(--brutal-ink)] flex-shrink-0" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-1 md:mb-2 break-words">500+</div>
              <div className="text-[var(--brutal-muted)] text-xs md:text-sm break-words">Store Locations</div>
            </Card>
            
            <Card className="brutal-panel p-4 md:p-6 text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 aspect-square brutal-chip mx-auto mb-3 md:mb-4 bg-[var(--brutal-mustard)]">
                <TrendUp className="w-6 h-6 md:w-8 md:h-8 text-[var(--brutal-ink)] flex-shrink-0" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-1 md:mb-2 break-words">40%</div>
              <div className="text-[var(--brutal-muted)] text-xs md:text-sm break-words">Efficiency Gain</div>
            </Card>
            
            <Card className="brutal-panel p-4 md:p-6 text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 aspect-square brutal-chip mx-auto mb-3 md:mb-4 bg-[var(--brutal-accent)]">
                <ChartBar className="w-6 h-6 md:w-8 md:h-8 text-[var(--brutal-ink)] flex-shrink-0" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-1 md:mb-2 break-words">90%</div>
              <div className="text-[var(--brutal-muted)] text-xs md:text-sm break-words">User Satisfaction</div>
            </Card>
          </div>

          {/* Curbside Pickup Interface Highlight */}
          <div className="mb-12">
            <Card variant="media" className="brutal-panel overflow-hidden !gap-0 !py-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                {/* Video Section */}
                <div className="relative min-h-80 h-full self-stretch bg-[var(--brutal-ink)] overflow-hidden">
                  <Components.VideoModal
                    dataPath="videos/SOCS-mobile-fast.mp4"
                    alt="Mobile curbside pickup interface showing order details, vehicle information, and inventory items ready for pickup"
                    poster="/assets/SOCS-inventory-scanning.png"
                    className="absolute inset-0 block w-full h-full"
                    requireAuth={false}
                  >
                    <div className="absolute inset-0 w-full h-full">
                      <img
                        src="/assets/SOCS-inventory-scanning.png"
                        alt="Mobile curbside pickup interface thumbnail"
                        className="absolute inset-0 w-full h-full object-cover object-[center_25%]"
                      />
                      <div className="absolute inset-0 bg-black/35 flex items-center justify-center pointer-events-none">
                        <div className="bg-white border-2 border-[var(--brutal-ink)] rounded-full p-6 shadow-[var(--brutal-shadow-sm)]">
                          <svg className="w-12 h-12 text-[var(--brutal-ink)]" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                      </div>
                      <div className="absolute top-4 left-4 bg-[var(--brutal-panel)] text-[var(--brutal-ink)] px-3 py-1 rounded-full text-sm font-medium border-2 border-[var(--brutal-ink)]">
                        Curbside Pickup Demo
                      </div>
                    </div>
                  </Components.VideoModal>
                </div>
                
                {/* Content Section */}
                <div className="p-8 flex flex-col justify-center">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 aspect-square brutal-chip bg-[#8fce8f] mr-4">
                      <DeviceMobile className="w-6 h-6 text-[var(--brutal-ink)]" />
                    </div>
                    <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[#8fce8f]">Mobile Solution</Badge>
                  </div>
                  
                  <h3 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4">Curbside Pickup Interface</h3>
                  
                  <p className="text-[var(--brutal-muted)] text-lg leading-relaxed mb-6">
                    Streamlined mobile interface designed for efficient curbside pickup operations, featuring real-time order tracking, vehicle identification, and inventory management.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-[var(--brutal-success)] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="text-[var(--brutal-ink)] font-semibold mb-1">Real-time Order Tracking</h4>
                        <p className="text-[var(--brutal-muted)] text-sm">Live updates on order status and pickup readiness</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-[var(--brutal-success)] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="text-[var(--brutal-ink)] font-semibold mb-1">Vehicle Identification</h4>
                        <p className="text-[var(--brutal-muted)] text-sm">Quick vehicle matching and customer verification</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-[var(--brutal-success)] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="text-[var(--brutal-ink)] font-semibold mb-1">Inventory Integration</h4>
                        <p className="text-[var(--brutal-muted)] text-sm">Seamless connection with store inventory systems</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-6 border-t border-[var(--brutal-ink)]">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-2xl font-bold text-[var(--brutal-ink)]">90%</div>
                        <div className="text-[var(--brutal-muted)] text-sm">Faster Pickup Process</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-[var(--brutal-ink)]">95%</div>
                        <div className="text-[var(--brutal-muted)] text-sm">Customer Satisfaction</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Project Overview */}
          <Tabs defaultValue="overview" className="mb-12">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview" className="truncate px-1 sm:px-3">Overview & Process</TabsTrigger>
              <TabsTrigger value="solution" className="truncate px-1 sm:px-3">Mobile Solution</TabsTrigger>
              <TabsTrigger value="results" className="truncate px-1 sm:px-3">Results & Impact</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="mt-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <Card className="brutal-panel p-8">
                  <div className="flex items-center mb-4">
                    <div className="w-3 h-3 bg-[var(--brutal-panel)] rounded-full mr-3" aria-hidden="true"></div>
                    <h3 className="text-2xl font-bold text-[var(--brutal-ink)]">The Challenge</h3>
                  </div>
                  <p className="text-[var(--brutal-muted)] leading-relaxed">
                    Store associates struggled with complex inventory management across multiple systems and platforms. The existing tools lacked mobile optimization, required extensive training, and created inconsistent user experiences across different retail operations, leading to inefficiencies and errors in inventory tracking.
                  </p>
                </Card>
                
                <Card className="brutal-panel p-8">
                  <div className="flex items-center mb-4">
                    <div className="w-3 h-3 bg-[var(--brutal-panel)] rounded-full mr-3" aria-hidden="true"></div>
                    <h3 className="text-2xl font-bold text-[var(--brutal-ink)]">The Vision</h3>
                  </div>
                  <p className="text-[var(--brutal-muted)] leading-relaxed">
                    Create a unified, mobile-first inventory management experience that would work seamlessly across all store operations. The goal was to design intuitive workflows that reduce training time, minimize errors, and provide consistent user experiences across different retail environments and device types.
                  </p>
                </Card>
              </div>

              <Components.KeyDecisions
                decisions={[
                  {
                    question: 'Desktop parity first, or mobile-first for the floor?',
                    tension:
                      'Matching the desktop tool would have felt complete. Associates live on handhelds in the aisle, where one-handed scan-and-enter is the real job.',
                    choice:
                      'We designed mobile-first for scanning and quick entry, then brought tablet and desktop into the same mental model.',
                  },
                  {
                    question: 'Separate apps per task, or one unified inventory experience?',
                    tension:
                      'Task-specific apps were easier to own per team. Associates still had to jump systems mid-shift, which trained in errors.',
                    choice:
                      'We unified inventory workflows so count, locate, and adjust felt like one product across store formats.',
                  },
                  {
                    question: 'Lab usability tests, or validate on the sales floor?',
                    tension:
                      'Lab sessions are cleaner and faster to schedule. They miss aisle noise, gloves, and the pace that breaks a tidy prototype.',
                    choice:
                      'We tested with associates in real stores and iterated on what failed under operational load.',
                  },
                ]}
              />
              
              <Card className="brutal-panel p-8">
                <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-6">Design Process</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 aspect-square brutal-chip bg-[var(--brutal-mustard)] font-bold text-sm">1</div>
                    <div>
                      <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Store Operations Research</h4>
                      <p className="text-[var(--brutal-muted)]">Conducted extensive field research in retail environments to understand current inventory workflows, pain points, and mobile usage patterns across different store formats.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 aspect-square brutal-chip bg-[var(--brutal-mustard)] font-bold text-sm">2</div>
                    <div>
                      <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Cross-Platform UX Audit</h4>
                      <p className="text-[var(--brutal-muted)]">Analyzed existing systems to identify inconsistencies and opportunities for UX unification across mobile, tablet, and desktop interfaces used in store operations.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 aspect-square brutal-chip bg-[var(--brutal-mustard)] font-bold text-sm">3</div>
                    <div>
                      <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Mobile-First Design</h4>
                      <p className="text-[var(--brutal-muted)]">Designed intuitive mobile interfaces optimized for one-handed operation, barcode scanning, and quick data entry in fast-paced retail environments.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 aspect-square brutal-chip bg-[var(--brutal-mustard)] font-bold text-sm">4</div>
                    <div>
                      <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Testing & Iteration</h4>
                      <p className="text-[var(--brutal-muted)]">Conducted usability testing in real store environments with actual associates, iterating on workflows and interface design based on operational feedback.</p>
                    </div>
                  </div>
                </div>
              </Card>
            </TabsContent>
            
            <TabsContent value="solution" className="mt-8">
              <Card className="brutal-panel p-8 mb-8">
                <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-6 text-center">Mobile-First Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Column 1: Inventory Scanning */}
                  <div className="brutal-panel rounded-xl overflow-hidden">
                    <div className="h-48 overflow-hidden">
                      <Components.ImageDisplay
                        src="https://images.unsplash.com/photo-1586953208448-b95a79798f07?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
                        alt="Inventory Scanning Interface"
                        className="w-full h-full object-cover transition-transform"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center mb-4">
                        <Package className="w-6 h-6 text-[var(--brutal-ink)] mr-3" />
                        <h4 className="text-lg font-semibold text-[var(--brutal-ink)]">Inventory Scanning</h4>
                      </div>
                      <p className="text-[var(--brutal-muted)] mb-4">Streamlined barcode scanning with real-time inventory updates and batch processing capabilities</p>
                      <div className="flex items-center text-[var(--brutal-success)] text-sm">
                        <TrendUp className="w-4 h-4 mr-2" />
                        60% faster inventory counts
                      </div>
                    </div>
                  </div>
                  
                  {/* Column 2: Order Management */}
                  <div className="brutal-panel rounded-xl overflow-hidden">
                    <div className="h-48 overflow-hidden">
                      <Components.ImageDisplay
                        src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80" 
                        alt="Order Management Interface"
                        className="w-full h-full object-cover transition-transform"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center mb-4">
                        <ShoppingCart className="w-6 h-6 text-[var(--brutal-success)] mr-3" />
                        <h4 className="text-lg font-semibold text-[var(--brutal-ink)]">Order Management</h4>
                      </div>
                      <p className="text-[var(--brutal-muted)] mb-4">Simplified ordering workflows with predictive suggestions and automated reorder points</p>
                      <div className="flex items-center text-[var(--brutal-success)] text-sm">
                        <TrendUp className="w-4 h-4 mr-2" />
                        45% reduction in stockouts
                      </div>
                    </div>
                  </div>
                  
                  {/* Column 3: Curbside Pickup */}
                  <div className="brutal-panel rounded-xl overflow-hidden">
                    <div className="h-48 overflow-hidden">
                      <Components.ImageDisplay
                        src="/assets/SOCS-inventory-scanning.png" 
                        alt="Curbside Pickup Interface"
                        className="w-full h-full object-cover transition-transform"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center mb-4">
                        <DeviceMobile className="w-6 h-6 text-[var(--brutal-ink)] mr-3" />
                        <h4 className="text-lg font-semibold text-[var(--brutal-ink)]">Curbside Pickup</h4>
                      </div>
                      <p className="text-[var(--brutal-muted)] mb-4">Streamlined mobile interface for curbside pickup operations with real-time order tracking</p>
                      <div className="flex items-center text-[var(--brutal-success)] text-sm">
                        <TrendUp className="w-4 h-4 mr-2" />
                        90% faster pickup process
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Subtle divider */}
              <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
              
              {/* UX Unification */}
              <div className="mb-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4">Cross-Platform UX Unification</h3>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="brutal-panel rounded-xl p-6 text-center">
                    <DeviceMobile className="w-12 h-12 text-[var(--brutal-ink)] mx-auto mb-4" />
                    <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Mobile Optimization</h4>
                    <p className="text-sm md:text-base text-[var(--brutal-ink)]">Touch-optimized interfaces designed for one-handed operation in busy store environments</p>
                  </div>
                  
                  <div className="brutal-panel rounded-xl p-6 text-center">
                    <Target className="w-12 h-12 text-[var(--brutal-success)] mx-auto mb-4" />
                    <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Consistent Patterns</h4>
                    <p className="text-sm md:text-base text-[var(--brutal-ink)]">Unified design language and interaction patterns across all platforms and devices</p>
                  </div>
                  
                  <div className="brutal-panel rounded-xl p-6 text-center">
                    <Lightning className="w-12 h-12 text-[var(--brutal-ink)] mx-auto mb-4" />
                    <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Workflow Integration</h4>
                    <p className="text-sm md:text-base text-[var(--brutal-ink)]">Seamless transitions between mobile and desktop workflows for comprehensive operations</p>
                  </div>
                </div>
              </div>
              
            </TabsContent>
            
            <TabsContent value="results" className="mt-8">
              <Card className="brutal-panel p-4 md:p-8 mb-8">
                <h3 className="text-xl md:text-2xl font-bold text-[var(--brutal-ink)] mb-4 md:mb-6">Business Impact & Results</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-4 border-b border-[var(--brutal-ink)]">
                    <div className="flex-1">
                      <h4 className="text-sm md:text-base font-semibold text-[var(--brutal-ink)] mb-1">Training Time</h4>
                      <p className="text-sm text-[var(--brutal-muted)]">Lower onboarding costs</p>
                    </div>
                    <div className="text-base md:text-2xl font-bold text-[var(--brutal-success)]">75% reduction</div>
                  </div>
                  
                  <div className="flex items-center justify-between py-4 border-b border-[var(--brutal-ink)]">
                    <div className="flex-1">
                      <h4 className="text-sm md:text-base font-semibold text-[var(--brutal-ink)] mb-1">Inventory Accuracy</h4>
                      <p className="text-sm text-[var(--brutal-muted)]">Reduced shrinkage</p>
                    </div>
                    <div className="text-base md:text-2xl font-bold text-[var(--brutal-success)]">11% improvement</div>
                  </div>
                  
                  <div className="flex items-center justify-between py-4 border-b border-[var(--brutal-ink)]">
                    <div className="flex-1">
                      <h4 className="text-sm md:text-base font-semibold text-[var(--brutal-ink)] mb-1">Task Completion Speed</h4>
                      <p className="text-sm text-[var(--brutal-muted)]">Higher productivity</p>
                    </div>
                    <div className="text-base md:text-2xl font-bold text-[var(--brutal-success)]">40% improvement</div>
                  </div>
                  
                  <div className="flex items-center justify-between py-4 border-b border-[var(--brutal-ink)]">
                    <div className="flex-1">
                      <h4 className="text-sm md:text-base font-semibold text-[var(--brutal-ink)] mb-1">User Satisfaction</h4>
                      <p className="text-sm text-[var(--brutal-muted)]">Better adoption</p>
                    </div>
                    <div className="text-base md:text-2xl font-bold text-[var(--brutal-success)]">25% increase</div>
                  </div>
                  
                  <div className="flex items-center justify-between py-4">
                    <div className="flex-1">
                      <h4 className="text-sm md:text-base font-semibold text-[var(--brutal-ink)] mb-1">Error Rate</h4>
                      <p className="text-sm text-[var(--brutal-muted)]">Improved accuracy</p>
                    </div>
                    <div className="text-base md:text-2xl font-bold text-[var(--brutal-success)]">75% reduction</div>
                  </div>
                </div>
              </Card>

              {/* Key Achievements */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card className="brutal-panel p-8">
                  <h3 className="text-xl font-semibold text-[var(--brutal-ink)] mb-6">Operational Improvements</h3>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-[var(--brutal-success)]" />
                      <span className="text-[var(--brutal-ink)]">Reduced training time from 8 hours to 2 hours</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-[var(--brutal-success)]" />
                      <span className="text-[var(--brutal-ink)]">Improved inventory accuracy by 11%</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-[var(--brutal-success)]" />
                      <span className="text-[var(--brutal-ink)]">40% faster task completion across all workflows</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-[var(--brutal-success)]" />
                      <span className="text-[var(--brutal-ink)]">75% reduction in operational errors</span>
                    </div>
                  </div>
                </Card>
                
                <Card className="brutal-panel p-8">
                  <h3 className="text-xl font-semibold text-[var(--brutal-ink)] mb-6">User Experience Wins</h3>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-[var(--brutal-success)]" />
                      <span className="text-[var(--brutal-ink)]">90% user satisfaction score</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-[var(--brutal-success)]" />
                      <span className="text-[var(--brutal-ink)]">Unified experience across 500+ store locations</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-[var(--brutal-success)]" />
                      <span className="text-[var(--brutal-ink)]">Mobile-first design adopted company-wide</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-[var(--brutal-success)]" />
                      <span className="text-[var(--brutal-ink)]">Cross-platform consistency achieved</span>
                    </div>
                  </div>
                </Card>
              </div>
            </TabsContent>
          </Tabs>

          {/* Key Learnings */}
          <div className="mb-12">
            {/* Subtle divider */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
            
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4">Key Learnings</h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="brutal-panel rounded-xl p-6">
                <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Mobile-First is Essential</h4>
                <p className="text-sm md:text-base text-[var(--brutal-ink)]">Store associates primarily use mobile devices, so designing mobile-first rather than adapting desktop interfaces was crucial for success.</p>
              </div>
              
              <div className="brutal-panel rounded-xl p-6">
                <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Context Matters</h4>
                <p className="text-sm md:text-base text-[var(--brutal-ink)]">Understanding the fast-paced, often chaotic retail environment was essential for designing interfaces that work in real-world conditions.</p>
              </div>
              
              <div className="brutal-panel rounded-xl p-6">
                <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Consistency Drives Adoption</h4>
                <p className="text-sm md:text-base text-[var(--brutal-ink)]">Unifying UX patterns across platforms dramatically reduced training time and improved user confidence across different tools.</p>
              </div>
            </div>
          </div>

          {/* Call-to-Action */}
          <Card className="brutal-panel p-8 text-center">
            <h3 className="text-3xl font-bold text-[var(--brutal-ink)] mb-4">Ready to optimize your mobile operations?</h3>
            <p className="text-[var(--brutal-muted)] mb-8 max-w-3xl mx-auto text-lg">
              I help organizations design mobile-first solutions that streamline operations and improve user experiences across all touchpoints.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                variant="outline" 
                className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] hover:text-[var(--brutal-ink)]"
              >
                <Link to="/ProfessionalProjects">View More Projects</Link>
              </Button>
              <Button 
                asChild 
                className="brutal-btn text-[var(--brutal-ink)] border-0"
              >
                <Link to="/Contact">Get In Touch</Link>
              </Button>
            </div>
          </Card>
        </div>
      </main>

      {/* Image Modal */}
      {selectedImage && (
        <Components.ImageModal
          src={selectedImage}
          alt="Store Inventory Operations"
        />
      )}
    </Components.PageContainer>
  )
}